import React, { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import DataTable from 'src/shared/components/data-table/DataTable';
import type { DataTableColumns, DataTableState, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import { TertiaryButton } from 'src/shared/components/button/Button';
import { Badge } from 'src/shared/components/ui/badge';
import styles from './TokenListTable.module.scss';
import type { ApiTokenSummary } from '../../state/api/engineApiTokensEngineSlice';

type TokenListTableProps = {
    tokens: ApiTokenSummary[];
    includeRevoked: boolean;
    onRowClick: (token: ApiTokenSummary) => void;
    onRevoke: (token: ApiTokenSummary) => void;
};

const tableColumns: DataTableColumns = [
    { columnId: 'id', title: 'Id', isUnique: true },
    { columnId: 'name', title: 'Name', isSortable: true, width: '180px' },
    { columnId: 'prefix', title: 'Prefix', width: '140px' },
    { columnId: 'scopes', title: 'Scopes', width: '220px' },
    { columnId: 'createdBy', title: 'Created by', isSortable: true, width: '220px' },
    { columnId: 'createdAt', title: 'Created at', isSortable: true, width: '140px' },
    { columnId: 'lastUsed', title: 'Last used', isSortable: true, width: '140px' },
    { columnId: 'status', title: 'Status', width: '100px' },
    { columnId: 'actions', title: 'Actions', width: '120px' },
];

const formatDate = (iso: string | null): string => {
    if (!iso) return 'Never';
    return format(parseISO(iso), 'd MMM yyyy');
};

const isRevoked = (token: ApiTokenSummary): boolean => token.revoked_at !== null;

const TokenListTable = ({ tokens, includeRevoked, onRowClick, onRevoke }: TokenListTableProps) => {
    const tableState: Partial<DataTableState> = useMemo(() => {
        const data = tokens.map(token => {
            const revoked = isRevoked(token);
            return [
                token.id,
                token.name,
                <code key='prefix' className='text-xs bg-grey-10 px-2 py-1 rounded'>
                    {token.token_prefix}
                </code>,
                <div key='scopes' className='flex flex-wrap gap-1'>
                    {token.allowed_scopes.map(scope => (
                        <span key={scope} className='text-xs bg-grey-10 px-2 py-0.5 rounded'>
                            {scope}
                        </span>
                    ))}
                </div>,
                token.created_by,
                formatDate(token.created_at),
                formatDate(token.last_used_at),
                revoked ? (
                    <Badge key='status' variant='error'>
                        Revoked
                    </Badge>
                ) : (
                    <Badge key='status' variant='success'>
                        Active
                    </Badge>
                ),
                revoked ? (
                    ''
                ) : (
                    <TertiaryButton
                        key='actions'
                        type='button'
                        className='text-red-100'
                        onClick={event => {
                            event.stopPropagation();
                            onRevoke(token);
                        }}
                        data-testid={`revoke-token-${token.id}`}
                    >
                        Revoke
                    </TertiaryButton>
                ),
            ];
        });
        return { data, hiddenColumnIds: new Set(['id']) };
    }, [tokens, onRevoke]);

    const handleRowSelect = (rowData: TableRowData) => {
        const id = rowData[0] as string;
        const token = tokens.find(t => t.id === id);
        if (token) onRowClick(token);
    };

    const emptyStateMessage = (
        <div className='flex flex-col items-center py-16 gap-2 text-center' data-testid='token-list-empty-state'>
            <h3 className='text-lg font-semibold text-primary'>{includeRevoked ? 'No revoked tokens' : 'No active tokens yet'}</h3>
            {!includeRevoked && <p className='text-sm text-grey-70'>Use &lsquo;New token&rsquo; (top-right) to create one.</p>}
        </div>
    );

    return (
        <div className={styles.tokenList}>
            <DataTable columns={tableColumns} state={tableState} emptyStateMessage={emptyStateMessage} onRowSelect={handleRowSelect} />
        </div>
    );
};

export default TokenListTable;
